import { useState } from "react";
import { Card } from "./Card";
import { Button } from "./Button";
import { Alert } from "./Alert";
import { useSaveDailyRecord } from "@/hooks/useDailyRecords";
import type { DailyRecordRow } from "@/lib/database.types";

// The four GLP-1 signals worth tracking daily: two tolerability (nausea,
// heartburn — what the clinician titrates on) and two efficacy (food noise,
// appetite suppression — what the patient actually feels working).
export const SYMPTOMS: Array<{ key: SymptomKey; label: string; hint: string }> = [
  { key: "nausea",               label: "Nausea",               hint: "0 none · 5 severe" },
  { key: "heartburn",            label: "Heartburn",            hint: "0 none · 5 severe" },
  { key: "food_noise",           label: "Food noise",           hint: "0 quiet · 5 constant" },
  { key: "appetite_suppression", label: "Appetite suppression", hint: "0 none · 5 strong" },
];
export type SymptomKey = "nausea" | "heartburn" | "food_noise" | "appetite_suppression";

const inputCls =
  "w-full rounded-[10px] border border-fh-border bg-white px-3.5 py-2.5 text-[15px] text-fh-ink " +
  "placeholder:text-fh-gray-light focus:outline-none focus:border-fh-green focus-visible:ring-2 focus-visible:ring-fh-green/30";
const labelCls = "block text-[12.5px] font-semibold text-fh-green-dark mb-1";

function prettyDay(dateISO: string): string {
  // record_date is a plain YYYY-MM-DD; anchor to noon to dodge TZ shifts.
  return new Date(`${dateISO}T12:00:00`).toLocaleDateString(undefined, {
    weekday: "long", month: "long", day: "numeric",
  });
}

interface Props {
  patientId: string | undefined;
  date: string; // YYYY-MM-DD
  record: DailyRecordRow | null;
  isToday: boolean;
}

/**
 * Editable check-in for one calendar day. Parent should key this component
 * by `date` (+ record id) so state re-initializes when the day changes.
 */
export function DailyCheckinCard({ patientId, date, record, isToday }: Props) {
  const save = useSaveDailyRecord(patientId);
  const [weight, setWeight] = useState(record?.weight_lb != null ? String(record.weight_lb) : "");
  const [calories, setCalories] = useState(record?.calories != null ? String(record.calories) : "");
  const [exercise, setExercise] = useState(
    record?.exercise_minutes != null ? String(record.exercise_minutes) : "",
  );
  const [scores, setScores] = useState<Record<SymptomKey, number | null>>({
    nausea: record?.nausea ?? null,
    heartburn: record?.heartburn ?? null,
    food_noise: record?.food_noise ?? null,
    appetite_suppression: record?.appetite_suppression ?? null,
  });
  const [notes, setNotes] = useState(record?.notes ?? "");
  const [savedFlash, setSavedFlash] = useState(false);

  const num = (s: string): number | null => {
    const n = Number(s);
    return s.trim() !== "" && Number.isFinite(n) && n >= 0 ? n : null;
  };

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    save.mutate(
      {
        record_date: date,
        weight_lb: num(weight),
        calories: num(calories),
        exercise_minutes: num(exercise),
        nausea: scores.nausea,
        heartburn: scores.heartburn,
        food_noise: scores.food_noise,
        appetite_suppression: scores.appetite_suppression,
        notes: notes.trim() || null,
      },
      {
        onSuccess: () => {
          setSavedFlash(true);
          window.setTimeout(() => setSavedFlash(false), 2500);
        },
      },
    );
  }

  return (
    <Card
      title={isToday ? `Today, ${prettyDay(date)}` : prettyDay(date)}
      action={
        savedFlash ? (
          <span className="text-fh-green-dark">Saved ✓</span>
        ) : record ? (
          <span className="text-[11px] font-semibold uppercase tracking-wider text-fh-gray">
            Logged
          </span>
        ) : undefined
      }
    >
      <form onSubmit={handleSubmit}>
        {save.error && (
          <div className="mb-3">
            <Alert tone="error">
              {save.error.message || "Couldn't save your check-in. Please try again."}
            </Alert>
          </div>
        )}

        {/* Metrics row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
          <div>
            <label htmlFor={`dc-weight-${date}`} className={labelCls}>Weight (lb)</label>
            <input
              id={`dc-weight-${date}`}
              type="number" inputMode="decimal" min="1" step="0.1"
              value={weight} onChange={(e) => setWeight(e.target.value)}
              placeholder="—" className={inputCls}
            />
          </div>
          <div>
            <label htmlFor={`dc-cal-${date}`} className={labelCls}>Calories</label>
            <input
              id={`dc-cal-${date}`}
              type="number" inputMode="numeric" min="0" step="1"
              value={calories} onChange={(e) => setCalories(e.target.value)}
              placeholder="—" className={inputCls}
            />
          </div>
          <div>
            <label htmlFor={`dc-ex-${date}`} className={labelCls}>Exercise (min)</label>
            <input
              id={`dc-ex-${date}`}
              type="number" inputMode="numeric" min="0" step="1"
              value={exercise} onChange={(e) => setExercise(e.target.value)}
              placeholder="—" className={inputCls}
            />
          </div>
        </div>

        {/* Symptom severities */}
        <div className="rounded-[12px] border border-fh-border bg-fh-cream p-3.5 mb-4">
          <div className="text-[12.5px] font-semibold text-fh-green-dark mb-2.5">
            How are you feeling?
          </div>
          <div className="space-y-3">
            {SYMPTOMS.map((s) => (
              <div key={s.key} className="flex items-center gap-3 flex-wrap">
                <div className="w-[172px] shrink-0">
                  <div className="text-[13px] font-semibold text-fh-ink leading-tight">{s.label}</div>
                  <div className="text-[10.5px] text-fh-gray-light">{s.hint}</div>
                </div>
                <div className="flex gap-1.5">
                  {[0, 1, 2, 3, 4, 5].map((n) => {
                    const active = scores[s.key] === n;
                    return (
                      <button
                        key={n}
                        type="button"
                        onClick={() =>
                          setScores((prev) => ({ ...prev, [s.key]: active ? null : n }))
                        }
                        aria-pressed={active}
                        aria-label={`${s.label} ${n} of 5`}
                        className={[
                          "w-8 h-8 rounded-full border text-[12.5px] font-semibold transition",
                          active
                            ? "border-fh-green bg-fh-green text-white"
                            : "border-fh-border bg-white text-fh-gray hover:border-fh-green-light",
                        ].join(" ")}
                      >
                        {n}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Notes */}
        <div className="mb-4">
          <label htmlFor={`dc-notes-${date}`} className={labelCls}>Notes for the day (optional)</label>
          <textarea
            id={`dc-notes-${date}`}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            placeholder="Meals, energy, anything your care team should know…"
            className={inputCls + " resize-y"}
          />
        </div>

        <div className="flex justify-end">
          <Button size="sm" type="submit" loading={save.isPending}>
            {record ? "Update check-in" : "Save check-in"}
          </Button>
        </div>
      </form>
    </Card>
  );
}
